import { useDebouncedCallback, useScreenResize } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Navigation } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { NavigationMenuDropdown } from './NavigationMenuDropdown';
import { NavigationMenuItem } from './NavigationMenuItem';

const DEBOUNCE_TIME = 100;
interface Props {
  navigationItems: Navigation.Item[];
}
export const NavigationMenu = ({ navigationItems }: Props) => {
  const { t } = useTranslation(['common']);

  const listRef = React.useRef<Nullable<HTMLUListElement>>(null);
  const [visibleItems, setVisibleItems] = React.useState(navigationItems);
  const navigationItemsToDisplay = React.useMemo(
    () => navigationItems.filter(item => item.type !== 'folder' || !isEmpty(item.children)),
    [navigationItems],
  );
  React.useEffect(() => {
    setVisibleItems(navigationItemsToDisplay);
  }, [navigationItemsToDisplay]);

  const setListRef = React.useCallback(
    (listElement: HTMLUListElement | null) => {
      if (!isDefined(listElement?.parentElement) || !isDefined(listElement)) {
        return;
      }

      listRef.current = listElement;

      const navElement = listElement.parentElement;
      const listItems = Array.from(listElement.children) as HTMLElement[];
      const availableWidth = navElement.offsetWidth;
      const isMoreItemVisible = visibleItems.length !== navigationItems.length;

      const { count: visibleItemsCount } = listItems.reduce(
        (acc, item) => {
          const itemWidth = item.offsetWidth;

          if (acc.width + itemWidth > availableWidth) {
            return acc;
          }

          return {
            count: acc.count + 1,
            width: acc.width + itemWidth,
          };
        },
        { count: isMoreItemVisible ? -1 : 0, width: 0 },
      );

      if (visibleItemsCount !== 0 && visibleItems.length !== visibleItemsCount) {
        setVisibleItems(navigationItemsToDisplay.slice(0, visibleItemsCount));
      }
    },
    [navigationItemsToDisplay, visibleItems.length],
  );

  const handleResize = React.useCallback(() => {
    setVisibleItems(navigationItemsToDisplay);
    setListRef(listRef.current);
  }, [setListRef, navigationItemsToDisplay]);

  useScreenResize(useDebouncedCallback(handleResize, DEBOUNCE_TIME));

  const moreItem: Navigation.FolderItem = {
    children: navigationItems.slice(visibleItems.length),
    label: t('common:more'),
    type: 'folder',
  };

  return (
    <nav className="flex-5 -my-1 flex justify-center overflow-auto py-1 no-scrollbar">
      <h2 className="sr-only">{t('common:main_menu')}</h2>
      <ul ref={setListRef} className="flex">
        {visibleItems.map((item, index) => {
          const shouldShowDropdown = item.type === 'folder' || (item.type === 'page' && !isEmpty(item.children));

          return (
            <li key={index} className="pr-8 last:pr-0">
              {!shouldShowDropdown ?
                <NavigationMenuItem item={item} />
              : <NavigationMenuDropdown isFolder={item.type === 'folder'} item={item}>
                  <NavigationMenuItem item={item} />
                </NavigationMenuDropdown>
              }
            </li>
          );
        })}
        {!isEmpty(moreItem.children) && (
          <li key="more" className="pr-8 last:pr-0">
            <NavigationMenuDropdown isFolder item={moreItem}>
              <NavigationMenuItem item={moreItem} />
            </NavigationMenuDropdown>
          </li>
        )}
      </ul>
    </nav>
  );
};
